import type { BetType } from '@casino/craps-engine';

export interface ZoneDef {
  /**
   * Stable identifier matching a key in `ZONE_META`. Help-mode UIs look up
   * `helpTitle`/`helpText` via `ZONE_META[zone.def.id]`.
   */
  id: string;
  type: BetType;
  number?: number;
  hopFaces?: [number, number];
  /** Polygon as flat number[] of [x, y, x, y, ...]. */
  polygon: number[];
  label: string;
  payoutHint: string;
  labelAnchor?: { x: number; y: number };
  /** Where the chip stack should rest, in canvas coords. */
  chipAnchor?: { x: number; y: number };
  /**
   * If false, skip rendering the chip stack on this zone — used for
   * alternate hit targets (e.g. don't-pass side extension) that share a
   * bet type with a canonical, larger zone.
   */
  showChipStack?: boolean;
  /**
   * If false, skip pointer hit-area + long-press wiring. Display-only zones
   * (e.g. the come-N number boxes that render an established come bet) use
   * this so clicks fall through to the underlying clickable zone — you
   * can't directly *place* a come bet on a number, it travels there.
   */
  interactive?: boolean;
}

export interface PanelDst { x: number; y: number; w: number; h: number; }
export interface SvgViewBox { x: number; y: number; w: number; h: number; }

export interface SvgFit {
  scale: number;
  offsetX: number;
  offsetY: number;
  scaledW: number;
  scaledH: number;
}

/** Letterbox-fit the original 800×400 SVG into the canvas. Retained for the
 *  landscape layout factory's sizing math. */
export function computeSvgFit(canvasW: number, canvasH: number): SvgFit {
  const SVG_W = 800;
  const SVG_H = 400;
  const scale = Math.min(canvasW / SVG_W, canvasH / SVG_H);
  const scaledW = SVG_W * scale;
  const scaledH = SVG_H * scale;
  return {
    scale,
    scaledW,
    scaledH,
    offsetX: (canvasW - scaledW) / 2,
    offsetY: (canvasH - scaledH) / 2,
  };
}

export interface ZoneMeta {
  type: BetType;
  number?: number;
  label: string;
  payoutHint: string;
  hopFaces?: [number, number];
  helpTitle: string;
  helpText: string;
  /** See ZoneDef.interactive — defaults to true. */
  interactive?: boolean;
}

/**
 * Per-SVG chip anchor maps. Each anchor is in the source SVG's coord space
 * (matches the data-bet rects). buildZonesFromSvg maps these into canvas
 * coords using the panel dst rect.
 */
const CHIP_ANCHORS_MAIN: Record<string, { x: number; y: number }> = {
  'place-4':    { x: 132.5, y: 105 },
  'place-5':    { x: 195.0, y: 105 },
  'place-6':    { x: 257.5, y: 105 },
  'place-8':    { x: 320.0, y: 105 },
  'place-9':    { x: 382.5, y: 105 },
  'place-10':   { x: 445.0, y: 105 },
  // Numbered come bets sit in the upper (dealer-side) sub-region of each
  // number box, so a numbered come bet doesn't visually collide with a
  // place chip on the same number.
  'come-4':     { x: 132.5, y: 50 },
  'come-5':     { x: 195.0, y: 50 },
  'come-6':     { x: 257.5, y: 50 },
  'come-8':     { x: 320.0, y: 50 },
  'come-9':     { x: 382.5, y: 50 },
  'come-10':    { x: 445.0, y: 50 },
  'dontCome':   { x: 82,    y: 110 },
  'come':       { x: 288.75, y: 175 },
  'field':      { x: 290, y: 235 },
  'dontPass':   { x: 320, y: 269 },
  'bigSix':     { x: 90,  y: 221 },
  'bigEight':   { x: 130, y: 262 },
};

const CHIP_ANCHORS_HARDWAY: Record<string, { x: number; y: number }> = {
  'any7':       { x: 622, y: 162 },
  'anyCraps':   { x: 622, y: 362 },
  'hardway-10': { x: 678,   y: 202 },
  'hardway-6':  { x: 565.6, y: 202 },
  'hardway-8':  { x: 565.6, y: 246 },
  'hardway-4':  { x: 678,   y: 246 },
  'aceDeuce':   { x: 547,   y: 290 },
  'aces':       { x: 622,   y: 290 },
  'boxcars':    { x: 697,   y: 290 },
  'yo':         { x: 565.6, y: 334 },
  'cAndE':      { x: 678,   y: 334 },
};

export const ZONE_META: Record<string, ZoneMeta> = {
  'place-4':  {
    type: 'place', number: 4, label: '4', payoutHint: '9:5',
    helpTitle: 'Place 4',
    helpText: 'A bet that the 4 will roll before a 7. Pays 9:5. You can take it down at any time. Place bets are off on the come-out roll by default.',
  },
  'place-5':  {
    type: 'place', number: 5, label: '5', payoutHint: '7:5',
    helpTitle: 'Place 5',
    helpText: 'A bet that the 5 will roll before a 7. Pays 7:5. You can take it down at any time. Place bets are off on the come-out roll by default.',
  },
  'place-6':  {
    type: 'place', number: 6, label: '6', payoutHint: '7:6',
    helpTitle: 'Place 6',
    helpText: 'A bet that the 6 will roll before a 7. Pays 7:6 — one of the best place bets. Bet in multiples of $6 to get the full payout.',
  },
  'place-8':  {
    type: 'place', number: 8, label: '8', payoutHint: '7:6',
    helpTitle: 'Place 8',
    helpText: 'A bet that the 8 will roll before a 7. Pays 7:6 — one of the best place bets. Bet in multiples of $6 to get the full payout.',
  },
  'place-9':  {
    type: 'place', number: 9, label: '9', payoutHint: '7:5',
    helpTitle: 'Place 9',
    helpText: 'A bet that the 9 will roll before a 7. Pays 7:5. You can take it down at any time. Place bets are off on the come-out roll by default.',
  },
  'place-10': {
    type: 'place', number: 10, label: '10', payoutHint: '9:5',
    helpTitle: 'Place 10',
    helpText: 'A bet that the 10 will roll before a 7. Pays 9:5. You can take it down at any time. Place bets are off on the come-out roll by default.',
  },
  'dontCome': {
    type: 'dontCome', label: "DON'T COME", payoutHint: '1:1',
    helpTitle: "Don't Come",
    helpText: "Like don't pass, but placed after the point is set. On the very next roll, a 2 or 3 wins immediately, a 7 or 11 loses, and 12 pushes; any other number becomes your don't-come point — you then win when a 7 rolls before that number repeats.",
  },
  'come':     {
    type: 'come', label: 'COME', payoutHint: '1:1',
    helpTitle: 'Come',
    helpText: 'Like the pass line, but placed after the point is set. The next roll becomes your come-bet point. Wins on 7 or 11 immediately, loses on 2/3/12, otherwise travels to the matching place number to win when it repeats before a 7.',
  },
  // come-N number boxes overlay the top half of place-N. They're interactive
  // so a tap there can back up an already-travelled come bet with odds. When
  // no come bet is on that number, the bridge falls the click through to a
  // place bet on the same number — net effect: the BOTTOM half is always a
  // place bet, and the TOP half is "come odds when applicable, place
  // otherwise". Phaser's input plugin gives the later-added zone (come-N
  // here, since the SVG declares come-N markers after place-N) priority on
  // overlapping clicks, which delivers the split.
  'come-4':   {
    type: 'come', number: 4, label: '', payoutHint: '1:1',
    helpTitle: 'Come (traveled to 4)',
    helpText: 'Your come bet picked up the 4 on the previous roll. It now wins if a 4 rolls before a 7, just like a place 4. Tap to back it up with odds.',
  },
  'come-5':   {
    type: 'come', number: 5, label: '', payoutHint: '1:1',
    helpTitle: 'Come (traveled to 5)',
    helpText: 'Your come bet picked up the 5 on the previous roll. It now wins if a 5 rolls before a 7, just like a place 5. Tap to back it up with odds.',
  },
  'come-6':   {
    type: 'come', number: 6, label: '', payoutHint: '1:1',
    helpTitle: 'Come (traveled to 6)',
    helpText: 'Your come bet picked up the 6 on the previous roll. It now wins if a 6 rolls before a 7, just like a place 6. Tap to back it up with odds.',
  },
  'come-8':   {
    type: 'come', number: 8, label: '', payoutHint: '1:1',
    helpTitle: 'Come (traveled to 8)',
    helpText: 'Your come bet picked up the 8 on the previous roll. It now wins if an 8 rolls before a 7, just like a place 8. Tap to back it up with odds.',
  },
  'come-9':   {
    type: 'come', number: 9, label: '', payoutHint: '1:1',
    helpTitle: 'Come (traveled to 9)',
    helpText: 'Your come bet picked up the 9 on the previous roll. It now wins if a 9 rolls before a 7, just like a place 9. Tap to back it up with odds.',
  },
  'come-10':  {
    type: 'come', number: 10, label: '', payoutHint: '1:1',
    helpTitle: 'Come (traveled to 10)',
    helpText: 'Your come bet picked up the 10 on the previous roll. It now wins if a 10 rolls before a 7, just like a place 10. Tap to back it up with odds.',
  },
  'field':    {
    type: 'field', label: 'FIELD', payoutHint: '1:1 (2x on 2, 3x on 12)',
    helpTitle: 'Field',
    helpText: 'A one-roll bet on 2, 3, 4, 9, 10, 11, or 12. Pays 1:1, with 2× on the 2 and 3× on the 12. Loses on 5, 6, 7, or 8.',
  },
  'dontPass': {
    type: 'dontPass', label: "DON'T PASS BAR", payoutHint: '1:1',
    helpTitle: "Don't Pass",
    helpText: "The opposite of the pass line. Wins on 2 or 3 on the come-out, loses on 7 or 11. The 12 is barred — it's a push. After a point is set, you win when a 7 rolls before the point repeats.",
  },
  'bigSix':   {
    type: 'bigSix', label: 'BIG 6', payoutHint: '1:1',
    helpTitle: 'Big 6',
    helpText: 'Bet that 6 rolls before 7. Pays 1:1 — strictly worse than a Place 6, which pays 7:6 for the same risk. Most experienced players skip this one.',
  },
  'bigEight': {
    type: 'bigEight', label: 'BIG 8', payoutHint: '1:1',
    helpTitle: 'Big 8',
    helpText: 'Bet that 8 rolls before 7. Pays 1:1 — strictly worse than a Place 8, which pays 7:6 for the same risk. Most experienced players skip this one.',
  },
  'any7':     {
    type: 'any7', label: 'ANY 7', payoutHint: '4:1',
    helpTitle: 'Any 7',
    helpText: 'A one-roll bet that the next roll is a 7. Pays 4:1. The house edge is among the highest on the table — fun to gamble on, expensive to lean on.',
  },
  'anyCraps': {
    type: 'anyCraps', label: 'ANY CRAPS', payoutHint: '7:1',
    helpTitle: 'Any Craps',
    helpText: 'A one-roll bet that the next roll is a 2, 3, or 12. Pays 7:1. Often used as protection on the come-out roll for a pass-line bettor.',
  },
  'hardway-4':  {
    type: 'hardway', number: 4, label: 'HARD 4', payoutHint: '7:1',
    helpTitle: 'Hard 4',
    helpText: 'Bet that 4 rolls as 2-2 (the "hard way") before a 7 or any other 4. Pays 7:1. Off on come-out unless you ask the dealer otherwise.',
  },
  'hardway-6':  {
    type: 'hardway', number: 6, label: 'HARD 6', payoutHint: '9:1',
    helpTitle: 'Hard 6',
    helpText: 'Bet that 6 rolls as 3-3 (the "hard way") before a 7 or any other 6. Pays 9:1. Off on come-out unless you ask the dealer otherwise.',
  },
  'hardway-8':  {
    type: 'hardway', number: 8, label: 'HARD 8', payoutHint: '9:1',
    helpTitle: 'Hard 8',
    helpText: 'Bet that 8 rolls as 4-4 (the "hard way") before a 7 or any other 8. Pays 9:1. Off on come-out unless you ask the dealer otherwise.',
  },
  'hardway-10': {
    type: 'hardway', number: 10, label: 'HARD 10', payoutHint: '7:1',
    helpTitle: 'Hard 10',
    helpText: 'Bet that 10 rolls as 5-5 (the "hard way") before a 7 or any other 10. Pays 7:1. Off on come-out unless you ask the dealer otherwise.',
  },
  'aceDeuce': {
    type: 'aceDeuce', label: '3', payoutHint: '15:1',
    helpTitle: 'Ace-Deuce (3)',
    helpText: 'A one-roll bet on 3 (1-2). Pays 15:1. High edge — a fun side bet, not a strategy.',
  },
  'aces':     {
    type: 'aces', label: 'ACES', payoutHint: '30:1',
    helpTitle: 'Aces (Snake Eyes)',
    helpText: 'A one-roll bet on 2 (1-1). Pays 30:1. Very high house edge — a flutter, not a plan.',
  },
  'boxcars':  {
    type: 'boxcars', label: '12', payoutHint: '30:1',
    helpTitle: 'Boxcars (12)',
    helpText: 'A one-roll bet on 12 (6-6). Pays 30:1. Very high house edge — a flutter, not a plan.',
  },
  'yo':       {
    type: 'yo', label: '11', payoutHint: '15:1',
    helpTitle: 'Yo (11)',
    helpText: 'A one-roll bet on 11. Pays 15:1. Often pressed by pass-line bettors on the come-out for the natural double-up.',
  },
  'cAndE':    {
    type: 'cAndE', label: 'C & E', payoutHint: '—',
    helpTitle: 'C & E',
    helpText: 'A two-part one-roll bet split between any-craps (2/3/12) and the 11. Wins differently on each side — see Any Craps and Yo for the components.',
  },
  'small':    {
    type: 'small', label: 'SMALL', payoutHint: '30:1+',
    helpTitle: 'Small',
    helpText: 'A bonus bet that all of 2, 3, 4, 5, and 6 will roll before any 7. Pays 30:1 (Standard), 35:1 (High Limit), or 40:1 (Salon Privé). House edge is steep — fun for the ride, expensive to lean on.',
  },
  'tall':     {
    type: 'tall', label: 'TALL', payoutHint: '30:1+',
    helpTitle: 'Tall',
    helpText: 'A bonus bet that all of 8, 9, 10, 11, and 12 will roll before any 7. Pays 30:1 (Standard), 35:1 (High Limit), or 40:1 (Salon Privé). High variance, satisfying when it hits.',
  },
  'all':      {
    type: 'all', label: 'ALL', payoutHint: '150:1+',
    helpTitle: 'All',
    helpText: 'A bonus bet that all of 2, 3, 4, 5, 6, 8, 9, 10, 11, and 12 will roll before any 7. Pays 150:1 (Standard), 175:1 (High Limit), or 200:1 (Salon Privé). The lottery moment of the table — long odds, big payoff.',
  },
};

/**
 * Required-number rows for each ATS zone. SMALL/TALL render as a single row
 * of 5; ALL renders as two rows of 5. Used by TableScene to construct
 * AtsBetZone instances.
 */
export const ATS_NUMBERS: Record<'small' | 'tall' | 'all',
  { numbers: number[]; twoRows: boolean }> = {
  small: { numbers: [2, 3, 4, 5, 6],                          twoRows: false },
  tall:  { numbers: [8, 9, 10, 11, 12],                       twoRows: false },
  all:   { numbers: [2, 3, 4, 5, 6, 8, 9, 10, 11, 12],        twoRows: true  },
};

export type AtsTierId = 'small' | 'all' | 'tall';

export interface AtsTierCell {
  id: AtsTierId;
  label: string;
  rect: { x: number; y: number; w: number; h: number };
}

export interface AtsStripLayout {
  cells: AtsTierCell[];
  numberFontSize: number;
}

/**
 * Compute per-tier cell rects and a shared number font size for the ATS
 * strip, given the strip's containing rect. Branches on aspect:
 *
 * - **Tall strip (portrait)**: cells stacked vertically. SMALL on top (30%),
 *   ALL middle (40% — needs the most height for its 5-tall number columns),
 *   TALL bottom (30%). Font derived from ALL's height ÷ 5.
 * - **Wide strip (landscape)**: three equal-width cells laid side-by-side
 *   (SMALL — ALL — TALL), each filling the full strip height. Font derived
 *   from `min(vertical-fit, horizontal-fit)` so the number column stays
 *   inside the cell at narrow viewports.
 *
 * Order is identical in both modes so the bridge / tutorial code can index
 * by tier id without caring about orientation.
 */
export function atsStripLayout(
  strip: { x: number; y: number; w: number; h: number },
  dpr: number,
): AtsStripLayout {
  const gap = Math.round(4 * dpr);
  const tiers: { id: AtsTierId; label: string }[] = [
    { id: 'small', label: 'SMALL' },
    { id: 'all',   label: 'ALL'   },
    { id: 'tall',  label: 'TALL'  },
  ];

  if (strip.w > strip.h) {
    // Horizontal: three equal-width cells, full strip height. Last cell
    // absorbs rounding so cumulative widths land exactly on strip.w.
    const availableW = strip.w - gap * 2;
    const cellW = Math.floor(availableW / 3);
    const cellH = strip.h;
    // Font: 4.8 = 2.5 (targetW/font) + 1.2 (2·sideGap-coeff) + 1.1
    // (2·numHalfW-coeff) — must mirror AtsBetZone's content-width math
    // or the columns drift past the cell edge.
    const verticalFit   = (cellH - 8 * dpr) * 0.14;
    const horizontalFit = (cellW - 10) / 4.8;
    const numberFontSize = Math.max(12, Math.floor(Math.min(verticalFit, horizontalFit)));
    let xCursor = strip.x;
    const cells: AtsTierCell[] = [];
    for (let i = 0; i < tiers.length; i++) {
      const t = tiers[i]!;
      const w = i < tiers.length - 1 ? cellW : strip.w - (xCursor - strip.x);
      cells.push({ id: t.id, label: t.label, rect: { x: xCursor, y: strip.y, w, h: cellH } });
      xCursor += w + gap;
    }
    return { cells, numberFontSize };
  }

  // Vertical: existing portrait stack. SMALL 30%, ALL 40%, TALL 30%.
  const availableH = strip.h - gap * 2;
  const smallH = Math.floor(availableH * 0.30);
  const allH   = Math.floor(availableH * 0.40);
  const tallH  = availableH - smallH - allH;
  const heights: Record<AtsTierId, number> = { small: smallH, all: allH, tall: tallH };
  const numberFontSize = Math.max(12, Math.round((allH / 5) * 0.7));
  let yCursor = strip.y;
  const cells: AtsTierCell[] = [];
  for (const t of tiers) {
    const h = heights[t.id];
    cells.push({ id: t.id, label: t.label, rect: { x: strip.x, y: yCursor, w: strip.w, h } });
    yCursor += h + gap;
  }
  return { cells, numberFontSize };
}

/** Parse "x y w h" from a viewBox attribute. */
function parseViewBox(attr: string | null): SvgViewBox {
  if (!attr) return { x: 0, y: 0, w: 1, h: 1 };
  const [x, y, w, h] = attr.trim().split(/\s+/).map(Number);
  return { x: x ?? 0, y: y ?? 0, w: w ?? 1, h: h ?? 1 };
}

/** Pick the chip-anchor map by inspecting which zone IDs are present. */
function chipAnchorsForId(id: string): { x: number; y: number } | undefined {
  return CHIP_ANCHORS_MAIN[id] ?? CHIP_ANCHORS_HARDWAY[id];
}

function rectPolygon(
  sx: (n: number) => number, sy: (n: number) => number,
  x: number, y: number, w: number, h: number,
): number[] {
  return [
    sx(x), sy(y),
    sx(x + w), sy(y),
    sx(x + w), sy(y + h),
    sx(x), sy(y + h),
  ];
}

/**
 * Build zones from a single SVG, mapping each `data-bet` marker into the
 * given panel-dst rect. The SVG's own viewBox attribute is used as the
 * source coord space.
 */
export function buildZonesFromSvg(svgText: string, panelDst: PanelDst): ZoneDef[] {
  const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
  const root = doc.documentElement;
  const viewBox = parseViewBox(root.getAttribute('viewBox'));
  const sx = (n: number) => panelDst.x + (n - viewBox.x) * (panelDst.w / viewBox.w);
  const sy = (n: number) => panelDst.y + (n - viewBox.y) * (panelDst.h / viewBox.h);

  const out: ZoneDef[] = [];
  const markers = Array.from(doc.querySelectorAll('[data-bet]'));
  for (const el of markers) {
    const key = el.getAttribute('data-bet');
    if (!key) continue;
    const meta = ZONE_META[key];
    if (!meta) continue;
    let polygon: number[] | null = null;
    const tag = el.tagName.toLowerCase();
    if (tag === 'rect') {
      const x = parseFloat(el.getAttribute('x') ?? '0');
      const y = parseFloat(el.getAttribute('y') ?? '0');
      const w = parseFloat(el.getAttribute('width') ?? '0');
      const h = parseFloat(el.getAttribute('height') ?? '0');
      polygon = rectPolygon(sx, sy, x, y, w, h);
    } else if (tag === 'polygon') {
      const raw = el.getAttribute('points') ?? '';
      const pairs = raw.trim().split(/\s+/);
      const pts: number[] = [];
      for (const pair of pairs) {
        const [px, py] = pair.split(',').map(Number);
        if (Number.isFinite(px) && Number.isFinite(py)) {
          pts.push(sx(px!), sy(py!));
        }
      }
      if (pts.length >= 6) polygon = pts;
    } else if (tag === 'circle') {
      const cx = parseFloat(el.getAttribute('cx') ?? '0');
      const cy = parseFloat(el.getAttribute('cy') ?? '0');
      const r = parseFloat(el.getAttribute('r') ?? '0');
      if (r > 0) {
        const segs = 24;
        const pts: number[] = [];
        for (let i = 0; i < segs; i++) {
          const a = (i / segs) * 2 * Math.PI;
          pts.push(sx(cx + r * Math.cos(a)), sy(cy + r * Math.sin(a)));
        }
        polygon = pts;
      }
    }
    if (!polygon) continue;
    const anchorSvg = chipAnchorsForId(key);
    const def: ZoneDef = {
      id: key,
      type: meta.type,
      polygon,
      label: meta.label,
      payoutHint: meta.payoutHint,
      ...(meta.number !== undefined ? { number: meta.number } : {}),
      ...(meta.hopFaces !== undefined ? { hopFaces: meta.hopFaces } : {}),
      ...(anchorSvg ? { chipAnchor: { x: sx(anchorSvg.x), y: sy(anchorSvg.y) } } : {}),
      ...(meta.interactive === false ? { interactive: false } : {}),
    };
    out.push(def);
  }
  return out;
}

/**
 * Build the procedural pass-line polygon, mapped into the given panel dst
 * using the panel's source-SVG viewBox.
 */
export function buildPassLineZone(panelDst: PanelDst, viewBox: SvgViewBox): ZoneDef {
  const sx = (n: number) => panelDst.x + (n - viewBox.x) * (panelDst.w / viewBox.w);
  const sy = (n: number) => panelDst.y + (n - viewBox.y) * (panelDst.h / viewBox.h);

  const cubic = (
    p0: [number, number], p1: [number, number],
    p2: [number, number], p3: [number, number],
    n: number,
  ): Array<[number, number]> => {
    const out: Array<[number, number]> = [];
    for (let i = 1; i <= n; i++) {
      const t = i / n;
      const u = 1 - t;
      const x = u*u*u*p0[0] + 3*u*u*t*p1[0] + 3*u*t*t*p2[0] + t*t*t*p3[0];
      const y = u*u*u*p0[1] + 3*u*u*t*p1[1] + 3*u*t*t*p2[1] + t*t*t*p3[1];
      out.push([x, y]);
    }
    return out;
  };
  const pts: Array<[number, number]> = [];
  // Shape derived from the actual SVG paths/lines:
  //   line161: outer top diagonal (100.751, 25)        → (25.064, 76.928)
  //   line163: outer left vertical (25.625, 75.836)    → (25.625, 250.836)
  //   path169: outer bezier corner  (25.625, 250.836)  → (100.625, 325.836)
  //   line165: outer bottom         (100.625, 325.836) → (477.061, 325.836)
  //   line167: right end up         (476.25, 325.836)  → (476.25, 287.711)
  //   line153: inner bottom-strip top (101.25, 287.711) ← (476.25, 287.711)
  //   path155: inner bezier corner  (101.25, 287.711)  → (63.75, 250.211)
  //   line151: inner left vertical  (63.75, 250.836)   → (63.75, 50.836)
  //   close diagonal back to (100.751, 25).
  pts.push([100.751, 25]);
  pts.push([25.064, 76.928]);
  pts.push([25.625, 250.836]);
  // Outer cubic corner: c 0,41.455 33.545,75 75,75 from (25.625, 250.836).
  for (const p of cubic(
    [25.625, 250.836], [25.625, 292.291],
    [59.17,  325.836], [100.625, 325.836], 12,
  )) pts.push(p);
  pts.push([476.25, 325.836]);
  pts.push([476.25, 287.711]);
  pts.push([101.25, 287.711]);
  // Inner cubic corner reversed: c 0,20.728 16.772,37.5 37.5,37.5 from (63.75, 250.211).
  // Forward end is (101.25, 287.711); we already pushed that as the previous point, so iterate t=1..0 (skipping t=1) and stop at t≈0.
  const innerForward = cubic(
    [63.75, 250.211], [63.75, 270.939],
    [80.522, 287.711], [101.25, 287.711], 12,
  );
  for (let i = innerForward.length - 2; i >= 0; i--) pts.push(innerForward[i]!);
  pts.push([63.75, 250.211]);
  pts.push([63.75, 50.836]);
  const flat: number[] = [];
  for (const [px, py] of pts) flat.push(sx(px), sy(py));

  return {
    id: 'pass',
    type: 'pass',
    polygon: flat,
    label: 'PASS LINE',
    payoutHint: '1:1',
    labelAnchor: { x: sx(330), y: sy(327) },
    chipAnchor: { x: sx(290), y: sy(307) },
  };
}

/**
 * Help text for objects that aren't BetZones — the procedural pass-line
 * polygon, the puck, the dice, and the help pill itself. The tutorial and
 * help-mode UI both use this dispatch table.
 */
export const NON_ZONE_HELP = {
  pass: {
    helpTitle: 'Pass Line',
    helpText: 'The most common bet. On the come-out roll, you win on 7 or 11 (pays 1:1) and lose on 2, 3, or 12. Any other number becomes the point — you then win when that point repeats before a 7.',
  },
  odds: {
    helpTitle: 'Odds (Backing the Line)',
    helpText: "Once the point is set, you can back your pass / come bet with ODDS — the best bet in the casino. Odds pay TRUE odds (2:1 on 4/10, 3:2 on 5/9, 6:5 on 6/8) with zero house edge. The 3-4-5× rule lets you take up to 3× the line on 4/10, 4× on 5/9, and 5× on 6/8. Tap the line again during point phase to back it up.",
  },
  dontOdds: {
    helpTitle: "Don't Odds (Laying)",
    helpText: "Backs a don't-pass / don't-come bet by LAYING odds — you put up more than you'll win since 7 is more likely than the point now. Pays true: 1:2 on 4/10, 2:3 on 5/9, 5:6 on 6/8. Cap is set so the max win matches the do-side cap (6× the line on a 3-4-5× table).",
  },
  puck: {
    helpTitle: 'The Puck',
    helpText: "Black/OFF means there is no point — the next roll is a come-out. White/ON sits on the current point number, telling you which number the shooter is trying to repeat before a 7.",
  },
  dice: {
    helpTitle: 'Roll the Dice',
    helpText: "Tap the dice to roll. The shooter must have a pass-line or don't-pass bet up to roll. The result is shown after the dice tumble — bets are paid out automatically.",
  },
  helpPill: {
    helpTitle: 'Help Mode',
    helpText: 'Tap the ? on any craps table, then tap any zone to see what it does. Tap ? again to exit.',
  },
} as const;
